import React, { useState } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useAdminTheme, tc } from './theme';

/**
 * Reusable "Generate image with AI" button + modal.
 *
 * Usage:
 *   <AiImageButton
 *     defaultPrompt="hero image for VoIP service page"
 *     folder="resources"
 *     onGenerated={(url) => setForm({ ...form, cover_image: url })}
 *   />
 *
 * Renders a single button. When clicked, opens a modal where the admin
 * enters a prompt + picks a style, then hits Generate. On success, the
 * persistent URL is passed to `onGenerated` and the modal closes.
 *
 * Provider is decided by the backend (`AI_IMAGE_PROVIDER` env var):
 *   - "pollinations" (default, free)
 *   - "openai"        (DALL-E 3, requires OPENAI_API_KEY)
 */
const STYLE_OPTIONS = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: '3D render', label: '3D render' },
  { value: 'flat illustration', label: 'Flat illustration' },
  { value: 'line art', label: 'Line art' },
  { value: 'minimalist vector', label: 'Minimalist vector' },
  { value: 'isometric illustration', label: 'Isometric illustration' },
  { value: 'watercolor', label: 'Watercolour' },
  { value: '', label: '(no style suffix)' },
];

const ASPECT_OPTIONS = [
  { value: 'square', label: 'Square 1:1 (1024×1024)', width: 1024, height: 1024 },
  { value: 'wide', label: 'Wide 16:9 (1792×1024)', width: 1792, height: 1024 },
  { value: 'tall', label: 'Tall 9:16 (1024×1792)', width: 1024, height: 1792 },
  { value: 'og', label: 'OpenGraph 1200×630', width: 1200, height: 630 },
];

export default function AiImageButton({
  defaultPrompt = '',
  folder = 'ai-images',
  onGenerated,
  size = 'md',
  label = 'Generate with AI',
  variant = 'accent',
}) {
  const { theme } = useAdminTheme();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [style, setStyle] = useState('photorealistic');
  const [aspect, setAspect] = useState('square');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const { width, height } =
        ASPECT_OPTIONS.find((a) => a.value === aspect) || ASPECT_OPTIONS[0];
      const res = await api.post('/api/admin-v2/ai/image', {
        prompt: prompt.trim(),
        style: style || undefined,
        width,
        height,
        folder,
      });
      const url = res.data?.url;
      if (!url) throw new Error('AI did not return a URL');
      onGenerated?.(url);
      setOpen(false);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sizeCls = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3 py-2 text-sm';
  const variantCls =
    variant === 'accent'
      ? theme === 'light'
        ? 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
        : 'bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 hover:text-white'
      : theme === 'light'
      ? 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
      : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10';

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setPrompt(defaultPrompt || prompt || '');
          setErr(null);
          setOpen(true);
        }}
        className={`inline-flex items-center gap-1.5 rounded-lg font-semibold whitespace-nowrap transition-all ${sizeCls} ${variantCls}`}
      >
        <Sparkles size={size === 'sm' ? 12 : 14} /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className={`w-full max-w-lg rounded-2xl p-6 shadow-2xl ${tc.cardBg(theme)}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
                  <Sparkles size={16} />
                </div>
                <div>
                  <p className={`font-bold ${tc.textPrimary(theme)}`}>Generate image with AI</p>
                  <p className={`text-[11px] ${tc.textMuted(theme)}`}>
                    Describe the image; pick a style + aspect ratio.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                className={`p-1 rounded-lg ${tc.hoverBg(theme)} ${tc.textMuted(theme)}`}
              >
                <X size={16} />
              </button>
            </div>

            <label
              className={`block text-[10px] uppercase tracking-widest font-bold mb-1 ${tc.textMuted(
                theme,
              )}`}
            >
              Prompt
            </label>
            <textarea
              rows={4}
              autoFocus
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A clean, modern illustration of a business phone system on a violet gradient background, no text, marketing style"
              className={`w-full px-3 py-2 rounded-lg outline-none text-sm resize-y ${tc.inputBg(theme)}`}
            />

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label
                  className={`block text-[10px] uppercase tracking-widest font-bold mb-1 ${tc.textMuted(
                    theme,
                  )}`}
                >
                  Style
                </label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg outline-none text-sm ${tc.inputBg(theme)}`}
                >
                  {STYLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className={`block text-[10px] uppercase tracking-widest font-bold mb-1 ${tc.textMuted(
                    theme,
                  )}`}
                >
                  Aspect
                </label>
                <select
                  value={aspect}
                  onChange={(e) => setAspect(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg outline-none text-sm ${tc.inputBg(theme)}`}
                >
                  {ASPECT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {err && (
              <div
                className={`mt-3 px-3 py-2 rounded-lg text-xs ${
                  theme === 'light'
                    ? 'bg-rose-50 text-rose-700 border border-rose-200'
                    : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
                }`}
              >
                {err}
              </div>
            )}

            <p className={`text-[11px] mt-3 ${tc.textDim(theme)}`}>
              Provider: <strong>Pollinations.ai</strong> (free) by default. Switch to OpenAI
              DALL-E 3 by setting <code>AI_IMAGE_PROVIDER=openai</code> +{' '}
              <code>OPENAI_API_KEY</code> in Coolify.
            </p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                disabled={busy}
                className={`text-sm font-semibold px-3 py-2 rounded-lg ${
                  theme === 'light'
                    ? 'text-slate-700 hover:bg-slate-100'
                    : 'text-slate-300 hover:bg-white/5'
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={run}
                disabled={busy || !prompt.trim()}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
